import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteRenderOptionState,
  FilterOptionsState,
  TextField,
  Theme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, {
  KeyboardEvent,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAppSelector, useShallowSelector } from '../../app/hooks';
import { ArrowKeys, Symbols } from '../../constants/enums';
import { orderActions } from '../../reducers/orderSlice';
import useOmniBarKeyboardActions from '../../hooks/useOmniBarKeyboardActions.hooks';
import {
  addCouponToCart,
  addLoyaltiesCode,
  applyLoyaltyCode,
  cartActions,
} from '../../reducers/cartSlice';
import {
  dialogActions,
  nextSlashCommand,
  NAVIGATION_LEVELS,
} from '../../reducers/dialogSlice';
import { addItemToCart } from '../../reducers/menuSlice';
import {
  AgentInterceptionType,
  InfoTransmissionMessage,
  messagingActions,
} from '../../reducers/messagingSlice';
import {
  selectDisableIntentInterval,
  selectFeatureFlagHitl3,
  selectFeatureFlagLoyaltySupport,
  selectFeatureFlagOCBReadback,
  selectFeatureFlagSuppressUpsell,
} from '../../redux/features/config/config.selector';
import {
  selectCartLoyalties,
  selectModGroupsWithDescriptions,
} from '../../selectors/cart';
import { selectDialog } from '../../selectors/dialog';
import useItemConversion from '../../hooks/useItemConversion.hooks';
import {
  generateMenuItemsSelector,
  getModSymbolMapping,
  posSettingsSelector,
  selectBMItemsSynonyms,
  selectBrandMenu,
  selectIsBrandMenu,
  selectIsExpressUpsellMode,
  selectIsExpressTimerActive,
  selectUnavailableItemsByName,
  selectTopLevelMenuItems,
} from '../../selectors/menu';
import { selectActiveRestaurantDetails } from '../../selectors/restaurant';
import { getItemUnavailableMessage } from '../../utils/TTSMessage';
import {
  findAnotherSelectedItem,
  getFilteredOptions,
  getOptionLabel,
  getQuantityFromInput,
  processQuantity,
} from '../../utils/autocomplete';
import Colors from '../../utils/color';
import {
  TTS_OFF_COMMAND,
  TTS_ON_COMMAND,
  SLASH_COMMAND_REMINDERS,
} from '../../utils/constants';
import {
  TopLevelMenuItem,
  isUnavailableItem,
  splitPathToItem,
} from '../../utils/menu';
import { AutocompleteOption } from './AutocompleteOption';
import useAutocompleteMessage from './hooks/useAutocompleteMessage';
import {
  FORCE_READBACK_HINT_MENU_ITEM,
  UpsellTriggers,
  FORCE_READBACK_HINT_MENU_ITEM_ID,
} from './Omnibar.constants';
import { ParsedMenuItem } from '../../types/menu';
import { upsell } from '../../utils/upsell';
import { v4 as uuidv4 } from 'uuid';
import { getMenuUnificationFeatureFlag } from '../../utils/session-storage';
import useModifierSelectionInfo from '../../hooks/useModifierSelectionInfo/useModifierSelectionInfo.hooks';
import {
  COUPON_LENGTH,
  LOYALTY_LENGTH,
  UPSELL,
  UPSELL_METRIC_VERSION,
  UPSELL_PREFIX,
} from '../../constants';
import {
  focusOmniBar,
  setOmniBarInput,
  somethingElseOwnsTheCaret,
} from '../../utils/omniBarFocus';
import useOmniBarFocusHandback from '../../hooks/useOmniBarFocusHandback.hooks';
import useAIEscalationUpsell from '../../hooks/useAIEscalationUpsell.hooks';
import { MOD_CODES_GROUP_ID } from '../../utils/menuUnification.constants';
import {
  processModifierSelection,
  shouldShowForceReadbackOmnibarHint,
  sortResults,
  checkEnterClicked,
} from './util/helper';
import { pickNextIndex } from '../../utils/common';
import { isBMModifierAnUnavailableConstraint } from '../../utils/menuUnification.utils';
import useSelectedCartItems from '../../hooks/useSelectedItems/useSelectedCartItems.hooks';
import useModifierGroupNavigation from '../../hooks/useModifierGroupNavigation.hooks';
import useEventListener from '../../hooks/useEventListener.hooks';
import { selectIntentCards } from '../../selectors/menu';
import { DEFAULT_INTENT_CARDS } from '../../constants/intents';
import { Intents } from '../../types/intent';
import useReadback from '../../hooks/useReadback/useReadback.hooks';
import { selectIsAgentActionBlocked } from '../../redux/features/agentMode/agentMode.selector';
import useScopedCartActions from '../../hooks/cartScope/useScopedCartActions.hooks';
import { DRAFT_CART_SCOPE } from '../../redux/features/draftCart/cartScope';
import { selectNewItemCartScope } from '../../redux/features/draftCart/cartScope.routing';

const useStyles = makeStyles((theme: Theme) => ({
  wrapText: {
    width: '100%',
    borderRadius: theme.spacing(1),
    backgroundColor: Colors.white,
    padding: theme.spacing(1),
    '& input': {
      paddingLeft: `${theme.spacing(1)} !important`,
    },
    '& .MuiOutlinedInput-notchedOutline.MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
  },
  option: {
    pointerEvents: 'none',
  },
}));

interface IAutocompleteComp {
  disabled: boolean;
}

/**
 * Claim the caret for the omnibar, ONCE, and register the node so anything that took
 * focus away can hand it back.
 *
 * ⚠️ ATTACH THIS AS A STABLE REFERENCE — `inputRef={focusOmniBarInput}` — never as an
 * inline arrow. React re-invokes a callback ref only when its identity changes, so a
 * stable reference runs once on mount and a fresh arrow each render runs on EVERY
 * render. The per-render version is what this app used to do, and it is not
 * survivable:
 *
 *  - It makes every other input on screen untypable. Focusing one dispatches, the
 *    store updates, this re-renders, and the caret is yanked back mid-keystroke.
 *    HITL-1501's flag panel has a free-text detail field, which is what surfaced it.
 *  - Worse, it fights any modal's focus trap. The omnibar is deliberately NOT on
 *    `AGENT_INTERCEPTION_IGNORED_SELECTORS`, so typing an order while the AI drives
 *    smoothly raises the "take over?" ConfirmDialog. MUI's TrapFocus pulls focus into
 *    the dialog, the next render pulls it back out, and the two loop: the caret sits
 *    visibly in the omnibar while every keystroke is swallowed. The box looks focused
 *    and accepts nothing.
 *
 * So focus is handed off EXPLICITLY at boundaries instead of grabbed continuously —
 * `focusOmniBar()` from `utils/omniBarFocus`. Explicit handoff at a known moment beats
 * a grab that races everything else on screen; two production focus bugs came out of
 * the grab.
 *
 * ## The boundary that matters most is a CLICK
 *
 * Removing the grab removed something agents depended on, and v6.3.0 shipped without
 * it: the whole TTS fast-path is bound to this input, so it works only while the input
 * has focus — Enter on an empty box (the auto-prompt / dialog-step advance, i.e. the
 * combo-size prompt), the four arrows, Shift+arrow, Shift+Backspace. The per-render
 * grab was silently keeping all of that alive, because the caret returned within a
 * render or two of any click. Without it, one click on a pill or a cart item left the
 * agent with no shortcuts until they clicked the box again — on every prompt, on live
 * calls.
 *
 * `useOmniBarFocusHandback` restores that, on a click rather than on a render. Read its
 * header before touching either: bubble phase and the deferral are both load-bearing.
 *
 * The guard is shared with `focusOmniBar` rather than reimplemented here — see
 * `somethingElseOwnsTheCaret`. It is also defence in depth for anyone who wires this
 * up per-render again.
 */
export const focusOmniBarInput = (input: HTMLInputElement | null) => {
  // Register the real node so anything that took focus away can hand it back. The
  // omnibar's `id` prop never reaches the DOM — MUI Autocomplete overrides it — so a
  // lookup by id is not an option. See utils/omniBarFocus.
  setOmniBarInput(input);
  if (!input) return;
  // The same predicate `focusOmniBar` uses, deliberately not a private copy: the two
  // v6.3.0 focus bugs both came from one focus path carrying a guard the other did
  // not.
  if (somethingElseOwnsTheCaret()) return;
  input.focus();
};

export const AutocompleteComp = ({
  disabled: disabledByProp,
}: IAutocompleteComp) => {
  // HITL-1606 disabled the omnibar outside `Intercept`, the same way a staff
  // intervention or a dropped socket disables it, and folded it into the
  // existing `disabled` so the two layouts (OmniBar / OmniBarCompact) cannot
  // drift.
  //
  // HITL-1619 takes `Shadow` back out of that. The omnibar is not one surface —
  // it adds items, selects modifiers, deletes lines and changes quantities, AND
  // it speaks to the customer, all from one input. A4 could not half-enable it
  // because the cart write path had no notion of a target slice, so "your cart
  // writes may proceed, your speech may not" was not expressible. HITL-1617's
  // routing makes it expressible, and building a parallel cart is the whole of
  // what `Shadow` is for — the copy control on the AI card is a shortcut for
  // when the AI happened to get a line right, never the way in.
  //
  // `Supervise` keeps the blanket disable: there is no My Cart there and
  // nothing to build into.
  const isAgentActionBlocked = useAppSelector(selectIsAgentActionBlocked);
  const newItemCartScope = useAppSelector(selectNewItemCartScope);
  const isBuildingOwnCart = newItemCartScope === DRAFT_CART_SCOPE;
  const disabled =
    disabledByProp || (isAgentActionBlocked && !isBuildingOwnCart);
  // Every path below that SPEAKS still checks this, so opening the input in
  // `Shadow` opens the cart half only.
  const isSpeechRefused = isAgentActionBlocked;
  const classes = useStyles();
  const dispatch = useDispatch();
  // Keeps the caret coming home after a click, which is what keeps Enter and the arrow
  // keys working without the agent aiming at the box first. See the hook's header.
  useOmniBarFocusHandback();
  // …and after the input comes back from disabled (staff intervention ended, socket
  // reconnected). The browser blurs a disabled input, and the node itself never
  // unmounts, so neither the mount ref nor `autoFocus` fires again — without this the
  // fast-path stays dead after every intervention until the agent clicks.
  const wasDisabledRef = useRef(disabled);
  useEffect(() => {
    if (wasDisabledRef.current && !disabled) {
      focusOmniBar();
    }
    wasDisabledRef.current = disabled;
  }, [disabled]);
  // HITL-1619 — two different questions, two different answers.
  //
  // `newItemCartScope` above says where a BRAND NEW item goes: the agent's
  // workspace, which is the draft in `Shadow` and the order in `Intercept`.
  // `selectedCartScope` here says where an edit to the item already open goes:
  // the cart that item actually lives in. They are usually the same, and must
  // not be assumed to be — an agent in `Shadow` can have the AI's line open.
  const {
    scope: selectedCartScope,
    actions: selectedCartActions,
    isDraft: isSelectedItemInDraft,
  } = useScopedCartActions();
  const { handleModifierSelection } = useItemConversion(selectedCartScope);
  const {
    currentMessage,
    updateCurrentMessage,
    sendMessage,
    sendDonationPromptMessage,
    isDonationPromptSkipped,
    sendAgentInterventionEvent,
    sendSlashCommandMessage,
  } = useAutocompleteMessage();
  const { canUpsellInAIEscalation } = useAIEscalationUpsell();
  const menuItems = useSelector(generateMenuItemsSelector(currentMessage));
  const isBrandMenu = useSelector(selectIsBrandMenu);
  const bmItemsSynonyms = useSelector(selectBMItemsSynonyms);
  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);
  const topLevelMenuItems = useSelector(selectTopLevelMenuItems);
  const cartLoyalties = useAppSelector(selectCartLoyalties);
  const handleLoyalty = useSelector(selectFeatureFlagLoyaltySupport);
  const isOCBReadbackEnabled = useAppSelector(selectFeatureFlagOCBReadback);
  const suppressUpsellEnabled = useAppSelector(selectFeatureFlagSuppressUpsell);
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const expressUpsellMode = useAppSelector(selectIsExpressUpsellMode);
  const expressTimerActive = useAppSelector(selectIsExpressTimerActive);
  const isUnifiedMenuEnabled = getMenuUnificationFeatureFlag();

  const { cart, selectedCartItem, selectedItemCartId } = useSelectedCartItems();
  const intentCardsFromBM = useAppSelector(selectIntentCards);
  const INTENT_CARDS = intentCardsFromBM ?? DEFAULT_INTENT_CARDS;

  const dialogState = useAppSelector(selectDialog);
  const {
    steps: dialogSteps,
    step: dialogStep,
    slashCommands,
    slashCommandIndex,
    voiceBoardValue,
    intentsStatus,
    selectedModGroup,
    navigationLevel,
  } = dialogState;
  const { restaurantName } = useSelector(selectActiveRestaurantDetails);
  const posSettings = useSelector(posSettingsSelector);
  const [inputQuantity, setInputQuantity] = useState(1);
  const [inputModSymbol, setInputModSymbol] = useState('');
  const [downTime, setDownTime] = useState(0);
  const [hasPreviousMessage, setHasPreviousMessage] = useState(false);
  const modSymbolMapping = useShallowSelector(getModSymbolMapping);

  /*
    Loyalty Code can be:
      - Legacy loyalty is of 6 digit coupon code
      - Panera with loyalty is of 10 digits phone number (Controlled by feature flag)
  */
  const [loyaltyCode, setLoyaltyCode] = useState<string | null>(null);

  // Coupon Code
  const [couponCode, setCouponCode] = useState<string | null>(null);

  // Customer Name
  const [customerName, setCustomerName] = useState<string>('');

  const [prefixWord, setPrefixWord] = useState('');
  const [handleOutOfOrderSearch, setHandleOutOfOrderSearch] = useState(false);
  const { arrowDown, arrowLeft, arrowRight, arrowUp } = ArrowKeys;
  const { processCartItemSelectionKey } = useOmniBarKeyboardActions();
  const { handleReadBack } = useReadback();
  const { sendModifierSelectionInfo } = useModifierSelectionInfo();

  const { handleKeyDown: handleModifierNavigation } =
    useModifierGroupNavigation({
      selectedCartItem,
    });

  // Tracks whether Alt (macOS Option) is held, for the Option-glyph guard in
  // `onTextChange`. Scoped to hitl_3_0: the Alt shortcuts that make agents press
  // Option+letter (Alt+1–8 / A/D / /) only exist in the new UX, so legacy keeps
  // its prior typing behaviour untouched. The listeners are always registered
  // (hooks can't be called conditionally) but no-op when the flag is off.
  // Must be a window CAPTURE listener, not the input's own keydown: the
  // AI-suggestion (Alt+A/D) and help (Alt+/) shortcuts also listen at
  // window-capture and call stopPropagation(), which would stop the event
  // before it reaches the input — so the input's keydown can't be relied on to
  // observe Alt. A sibling window-capture listener still fires regardless
  // (stopPropagation doesn't stop siblings on the same target). `KeyboardEvent`
  // here is React's synthetic type, so altKey is read off the native event.
  const altDownRef = useRef(false);
  useEventListener(
    'keydown',
    (e) => {
      if (!isHitl3) return;
      altDownRef.current = (e as unknown as { altKey: boolean }).altKey;
    },
    window,
    true
  );
  useEventListener(
    'keyup',
    (e) => {
      if (!isHitl3) return;
      altDownRef.current = (e as unknown as { altKey: boolean }).altKey;
    },
    window,
    true
  );

  useEffect(() => {
    if (currentMessage) {
      setHasPreviousMessage(true);
    } else {
      let timer = setTimeout(() => {
        setHasPreviousMessage(false);
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [currentMessage]);

  const modGroupsData = useSelector(selectModGroupsWithDescriptions);
  const {
    allModGroups: autoPromptModGroups,
    allDescriptions: autoPromptModGroupDescriptions,
  } = modGroupsData;

  const autoPromptModGroupDescriptionsToJSONString = JSON.stringify(
    autoPromptModGroupDescriptions
  );
  useEffect(() => {
    if (autoPromptModGroupDescriptions.length)
      dispatch(
        dialogActions.addInvalidModGroupDescriptions(
          autoPromptModGroupDescriptions
        )
      );
  }, [autoPromptModGroupDescriptionsToJSONString]);

  const getDialogMessage = (triggerUpsellAttempt: boolean = false) => {
    if (autoPromptModGroups.length && autoPromptModGroupDescriptions.length) {
      const firstAutoPromptModGroup = autoPromptModGroups[0];
      const firstAutoPromptDescription = autoPromptModGroupDescriptions[0];
      if (
        triggerUpsellAttempt &&
        isUnifiedMenuEnabled &&
        firstAutoPromptModGroup?.is_upsell_prompt
      ) {
        const upsell_attempted = upsell.attempt(
          firstAutoPromptModGroup,
          selectedCartItem?.name || '',
          UpsellTriggers.hitlOmnibar,
          firstAutoPromptModGroup.description || '.',
          firstAutoPromptModGroup.name
        );
        return {
          message: firstAutoPromptModGroup.description || '.',
          // Only send a lightweight identifier on the HITL text event.
          // The actual upsell_attempted METRIC will be emitted later as an
          // INFO event once ORDERBOARD confirms the prompt was not blocked.
          metadata: {
            upsell_id: upsell_attempted.upsell_id,
          },
        };
      }
      return { message: firstAutoPromptDescription };
    } else {
      // If there are slash commands and we haven't processed all of them, use the current one
      if (
        slashCommands.length > 0 &&
        slashCommandIndex < slashCommands.length
      ) {
        return {
          message: slashCommands[slashCommandIndex].command,
        };
      }

      // Otherwise, use normal dialog steps logic
      const hasItemsInCart = Object.keys(cart.cartItems).length > 0;
      if (hasItemsInCart) {
        // When cart has items, use "Sure thing." (index 1)
        return {
          message: dialogSteps[1]
            .split('{RESTAURANT_NAME}')
            .join(restaurantName),
        };
      }

      // Default: loop through dialog steps when cart is empty
      return {
        message: dialogSteps[dialogStep]
          .split('{RESTAURANT_NAME}')
          .join(restaurantName),
      };
    }
  };

  const DISABLE_INTENT_INTERVAL = useAppSelector(selectDisableIntentInterval);

  const handleKeyPress = (event: KeyboardEvent) => {
    const payload: Partial<InfoTransmissionMessage> = {
      data: { message: 'Key ' + event.key + ' pressed', type: 'METRIC' },
    };
    dispatch(messagingActions.sendInfo(payload as any));
    if (event.key === 'Enter') {
      event.preventDefault();
      !checkEnterClicked.getIsEnterClicked() && handleEnterPress(event);
    }
  };

  const handleEnterPress = (event: KeyboardEvent) => {
    if (loyaltyCode) {
      if (loyaltyCode.length <= COUPON_LENGTH) {
        // Call Apply Loyalty Code API
        dispatch(applyLoyaltyCode({ loyaltyCode }));
      } else {
        // For panera 10 digit phone number send loyalty code inside order event
        if (!cartLoyalties?.find(({ code }) => code === loyaltyCode)) {
          dispatch(addLoyaltiesCode({ code: loyaltyCode }));
        }
      }
    } else if (couponCode) {
      dispatch(addCouponToCart(couponCode.toString()));
      setCouponCode(null); // clear coupon state after adding to cart
    } else if (customerName) {
      // Set customer name in Redux state
      dispatch(orderActions.setCustomerName(customerName));
      setCustomerName(''); // clear customer name state after setting
      updateCurrentMessage('');
    } else if (!event.shiftKey) {
      // HITL-1619: everything below this line is SPEECH — the dialog prompt, a
      // slash command, the donation/upsell auto-prompt — plus the dialog-state
      // advancement that records what was said. Outside `Intercept` none of it
      // may happen, and it has to be refused HERE rather than left to the
      // middleware's TTS sink: the branch also ADDS the donation or
      // end-of-order upsell item to the cart on the strength of having spoken
      // the prompt (`shouldAddToCart` below). Swallow the speech alone and the
      // item lands in the cart for an offer the customer never heard — which is
      // exactly the invariant HITL-1570 wrote that guard for.
      if (isSpeechRefused) return;
      if (voiceBoardValue !== '' && voiceBoardValue[0] === '/') {
        handleMessageSubmit();
      } else if (!event.shiftKey) {
        const { message, metadata } = getDialogMessage(true);

        // HITL-1570: a donation slash command whose constraint declares
        // speak_total_in_upsell is gated on a check total being available —
        // skipped entirely without one (the reminder hook already filters it
        // out at seed time; this guards the race where the queue was seeded
        // before the total went away or the menu changed), and recorded as
        // having spoken the total otherwise, so Complete can skip repeating
        // it. Mirrors getDialogMessage's branch order: auto-prompt wins over
        // the queue.
        const isAutoPromptActive =
          autoPromptModGroups.length > 0 &&
          autoPromptModGroupDescriptions.length > 0;
        const currentQueuedCommand =
          !isAutoPromptActive &&
          slashCommands.length > 0 &&
          slashCommandIndex < slashCommands.length
            ? slashCommands[slashCommandIndex]
            : undefined;

        // Shared lookup for the send gate and the add-to-cart block below —
        // both need the top-level item behind the queued command's intent.
        const findActiveTopLevelItemByIntent = (intentName: string) =>
          Object.values(topLevelMenuItems).find(
            (item) =>
              !item.inactive &&
              (item.name === intentName || item.name.startsWith(intentName))
          );

        // Only DONATION (exact match) and UPSELL_END_OF_ORDER (prefix match)
        // are added to the cart once their prompt is spoken.
        const isCartAddIntent = (intent: string) =>
          intent === SLASH_COMMAND_REMINDERS.DONATION ||
          intent.startsWith(SLASH_COMMAND_REMINDERS.UPSELL_END_OF_ORDER_PREFIX);

        // HITL-1700: the cart add below is what creates the upsell attempt, and
        // it runs AFTER the prompt is spoken — so the prompt went out with no
        // upsell_id, the TTS acknowledgement could never match it, and the
        // attempt METRIC was never sent while the customer's answer still was.
        // Mint the id now, put it on the prompt, and hand it to the cart add so
        // the attempt it creates carries the same id.
        const queuedUpsellId =
          currentQueuedCommand && isCartAddIntent(currentQueuedCommand.intent)
            ? uuidv4()
            : undefined;
        const promptMetadata = queuedUpsellId
          ? { ...metadata, upsell_id: queuedUpsellId }
          : metadata;

        // A skipped donation must not be spoken NOR added to the cart — only
        // consumed from the queue (below), so Enter moves on to whatever is
        // next.
        let donationSkipped = false;
        if (currentQueuedCommand?.intent === SLASH_COMMAND_REMINDERS.DONATION) {
          const donationItem = findActiveTopLevelItemByIntent(
            currentQueuedCommand.intent
          );
          donationSkipped = !sendDonationPromptMessage(
            message,
            donationItem?.speakTotalInUpsell,
            promptMetadata
          );
        } else {
          sendMessage(message, promptMetadata);
        }

        // Handle dialog state advancement (same priority as getDialogMessage)
        if (
          autoPromptModGroups.length &&
          autoPromptModGroupDescriptions.length
        ) {
          // Auto-prompt mode - don't advance dialog state (auto-prompt handles its own flow)
        } else if (
          slashCommands.length > 0 &&
          slashCommandIndex < slashCommands.length
        ) {
          // Auto-add items to cart when their prompts are sent (only for DONATION and UPSELL_END_OF_ORDER)
          const currentSlashCommand = slashCommands[slashCommandIndex];

          // Use the intent from the slash command to check if it matches DONATION or UPSELL_END_OF_ORDER
          const intent = currentSlashCommand.intent;

          // HITL-1570: a donation skipped for lack of a check total was never
          // offered, so it must not land in the cart either.
          const shouldAddToCart = !donationSkipped && isCartAddIntent(intent);

          if (shouldAddToCart) {
            // Find the corresponding top-level menu item by intent/name
            const matchingItem = findActiveTopLevelItemByIntent(intent);

            if (matchingItem) {
              dispatch(
                addItemToCart({
                  menuItem: matchingItem,
                  quantity: 1,
                  byPassAutoPrompt: true, // TTS already be handled above so skip it in the thunk call
                  upsellId: queuedUpsellId,
                })
              );
              sendAgentInterventionEvent(
                AgentInterceptionType.ORDER_CHANGED,
                `${matchingItem.name} added to cart`
              );
            }
          }

          // Advance to the next slash command
          dispatch(nextSlashCommand());
        } else {
          // Normal dialog steps mode
          if (dialogStep === dialogSteps.length - 1) {
            if (dialogStep !== 0) {
              dispatch(dialogActions.setStep(0));
            }
          } else {
            dispatch(dialogActions.increaseStep());
          }
        }
      }
    }
    checkEnterClicked.setIsEnterClicked(true);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    // Handle modifier group navigation first
    handleModifierNavigation(event);

    if ([arrowDown, arrowLeft, arrowRight, arrowUp, ' '].includes(event.key)) {
      if (event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (event.key !== ' ' && !downTime) setDownTime(new Date().valueOf()); // Arrow keys use key press duration to select the TTS message
    }
  };

  const sendTTSByUsingArrowKeys = (event: KeyboardEvent) => {
    // HITL 3.0 only: a disabled omnibar (staff intervention / socket disconnect)
    // must never fire intent TTS via the arrow fast-path — even though the
    // focused input can still surface keyup events. Gated on isHitl3 so the
    // legacy layout's arrow behavior is left exactly as-is. (Shift+arrow cart
    // navigation is a separate path and stays available in both layouts.)
    if (isHitl3 && disabled) {
      setDownTime(0);
      return;
    }
    let upTime = new Date().valueOf();
    const timePressed = Math.floor((upTime - downTime) / 100);
    const getMessage = (card: number) => {
      const messages = INTENT_CARDS[card].messages;
      const messageIndex = pickNextIndex(
        messages.length,
        intentsStatus[card]?.lastMessageIndex
      );
      let message = '';
      if (messageIndex !== undefined) {
        message = messages[messageIndex];
        dispatch(
          dialogActions.setIntentLastMessageIdx({
            idx: card,
            lastMessageIndex: messageIndex,
          })
        );
      }
      return message;
    };

    // Resolve the arrow's target intent by its semantic enum, then find that
    // card's position in the active list — so a BM-customized / reordered set
    // can't silently send the wrong message (this mapping used to be hardcoded
    // to default positions 1/3/5/7).
    const arrowIntentMap: Partial<Record<ArrowKeys, Intents>> = {
      [arrowUp]: Intents.acknowledge, // Okay
      [arrowLeft]: Intents.askMoment, // Moment
      [arrowRight]: Intents.continue, // Continue
      [arrowDown]: Intents.readback, // Read Back
    };
    const targetIntent = arrowIntentMap[event.key as ArrowKeys];
    const intentIndex = targetIntent
      ? INTENT_CARDS.findIndex((card) => card.intent === targetIntent)
      : -1;

    // HITL-1619: the arrow-key fast-path to an intent. Every intent speaks, so
    // it is refused outside `Intercept` — guarded here rather than only at the
    // TTS sink so the intent's own cool-down and `agent_interception` do not
    // fire for something the customer never heard. Same shape as A4's guard on
    // `useGroupedIntents`, which `useIntentHotkeys` reached past.
    if (
      intentIndex >= 0 &&
      intentsStatus[intentIndex]?.clickable &&
      !isSpeechRefused
    ) {
      if (targetIntent === Intents.readback) {
        handleReadBack({ timePressed });
        sendAgentInterventionEvent(AgentInterceptionType.TTS_SENT);
      } else {
        sendMessage(getMessage(intentIndex));
      }
      dispatch(
        dialogActions.setIntentClickable({ idx: intentIndex, status: false })
      );
      setTimeout(() => {
        dispatch(
          dialogActions.setIntentClickable({ idx: intentIndex, status: true })
        );
      }, DISABLE_INTENT_INTERVAL);
    }

    setDownTime(0);
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    if (!currentMessage) {
      event.preventDefault();
      if (
        Object.values(ArrowKeys).includes(event.key as ArrowKeys) &&
        !event.shiftKey
      )
        if (!isSpeechRefused)
          // Arrow keys with an empty omnibar speak an intent — `Intercept` only.
          sendTTSByUsingArrowKeys(event);
    }

    if (event.shiftKey) {
      event.preventDefault();
      // Use Shift+Backspace key for removing last cart item
      if (!currentMessage && !hasPreviousMessage) {
        if (
          event.key === 'Backspace' &&
          selectedCartItem &&
          selectedItemCartId > 0
        ) {
          dispatch(
            selectedCartActions.deleteCartItem(selectedItemCartId.toString())
          );
          // Nothing in the draft has been ordered, so there is no order change
          // to announce — and in `Intercept` announcing would latch
          // `agentMode.engaged` off a private edit. Same rule the centre pane
          // follows (HITL-1617).
          if (!isSelectedItemInDraft)
            sendAgentInterventionEvent(
              AgentInterceptionType.ORDER_CHANGED,
              `${selectedCartItem.name} removed from order`
            );

          const newCartItemId = findAnotherSelectedItem(
            cart.cartItems,
            selectedItemCartId
          );
          if (newCartItemId > 0) {
            dispatch(
              dialogActions.selectItemAndResetNavigation({
                item: cart.cartItems[newCartItemId.toString()],
                itemCartId: newCartItemId,
                // `cart` is the scoped cart, so the neighbour we fall back to
                // belongs to the cart we just deleted from.
                cartScope: selectedCartScope,
              })
            );
          } else {
            dispatch(dialogActions.selectItemAndResetNavigation(undefined));
          }

          if (isUnifiedMenuEnabled) {
            // Check upsell and auto reject it if the cart context changes
            upsell.settle();
          }
        }
        if (
          [ArrowKeys.arrowDown, ArrowKeys.arrowUp].includes(event.key as any)
        ) {
          processCartItemSelectionKey(event.key);
        }
      }
    }
    checkEnterClicked.setIsEnterClicked(false);
  };

  const handleMessageSubmit = () => {
    if (voiceBoardValue.length > 0) {
      //Send message only when the agent typed '//ttson' for TTS-ON and '/\ttsoff' for TTS-OFF
      if (voiceBoardValue === TTS_OFF_COMMAND) {
        sendMessage('\\'); // The backslash ("\") character is a special escape character. To use backslash character itself, two backslashes are needed.
      }

      if (voiceBoardValue === TTS_ON_COMMAND) {
        sendMessage('/');
      }

      updateCurrentMessage('');
      dispatch(dialogActions.setVoiceBoardValue(''));
    }
  };

  const onTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // macOS commits Option+<key> glyphs (å, ¡, ÷, …) into the focused input via
    // the text-input service, which a keydown preventDefault can't stop (so
    // Alt+digit never leaked but Alt+letter did). This is the single funnel that
    // persists omnibar text, so refusing the change while Alt is held reverts
    // the controlled value and the glyph can never stick. The Alt shortcuts
    // (1–8 / A / D / /) fire on their own keydown handlers, untouched by this.
    // Gated to hitl_3_0 — those shortcuts only exist in the new UX, so legacy
    // typing is unchanged (altDownRef also stays false there; see tracker above).
    if (isHitl3 && altDownRef.current) {
      return;
    }
    const curValue: any = event.currentTarget.value;

    // Agent Interception TYPING event
    if (!currentMessage && curValue) {
      sendAgentInterventionEvent(AgentInterceptionType.TYPING);
    }

    if (
      curValue &&
      [
        Symbols.star,
        Symbols.plus,
        Symbols.minus,
        Symbols.caret,
        Symbols.at,
      ].includes(curValue[0])
    ) {
      const firstChar = curValue[0];
      if ([Symbols.star, Symbols.caret].includes(firstChar)) {
        //Logic related to coupon code
        const couponNum = curValue.slice(1);
        if (!isNaN(couponNum)) {
          if (
            firstChar === Symbols.star &&
            couponNum.length <= (handleLoyalty ? LOYALTY_LENGTH : COUPON_LENGTH)
          ) {
            setLoyaltyCode(couponNum);
            if (couponCode) setCouponCode(null);
            if (customerName) setCustomerName('');
            updateCurrentMessage(Symbols.star + couponNum);
          } else if (firstChar === Symbols.caret) {
            // use caret key: ^ to enter coupon besides loyalty
            setCouponCode(couponNum);
            if (loyaltyCode) setLoyaltyCode(null);
            if (customerName) setCustomerName('');
            updateCurrentMessage(Symbols.caret + couponNum);
          }
        }
      } else if (firstChar === Symbols.at) {
        // use @ symbol to enter customer name
        const name = curValue.slice(1);
        setCustomerName(name);
        if (loyaltyCode) setLoyaltyCode(null);
        if (couponCode) setCouponCode(null);
        updateCurrentMessage(Symbols.at + name);
      } else if ([Symbols.plus, Symbols.minus].includes(firstChar)) {
        updateCurrentMessage(curValue);
      }
    } else {
      updateCurrentMessage(curValue);
      setLoyaltyCode(null);
      setCouponCode(null);
      setCustomerName('');
      dispatch(dialogActions.setVoiceBoardValue(curValue));
    }
  };

  const setCurrentMessageAndVoiceBoardValue = (
    message = '',
    voiceBoardValue = ''
  ) => {
    updateCurrentMessage(message);
    dispatch(dialogActions.setVoiceBoardValue(voiceBoardValue));
  };

  const onSelectChange = (
    event: any,
    value: ParsedMenuItem | string | TopLevelMenuItem | null,
    reason: AutocompleteChangeReason
  ) => {
    if (value === null) {
      setCurrentMessageAndVoiceBoardValue();
    } else if (typeof value === 'string') {
      setCurrentMessageAndVoiceBoardValue(value, value);
    } else if (value instanceof Object) {
      if (value.id === FORCE_READBACK_HINT_MENU_ITEM_ID) {
        // Reading the order back is speech, and speech is `Intercept` only.
        if (isSpeechRefused) {
          setCurrentMessageAndVoiceBoardValue();
          return;
        }
        handleReadBack({ timePressed: 0, forceRegularReadback: true });
        sendAgentInterventionEvent(AgentInterceptionType.TTS_SENT);
        setCurrentMessageAndVoiceBoardValue();
        return;
      }
      // Finding whether item is unavailable (or) not based on brand menu
      const getBMUnavailability = () => {
        if (value.pathToItem && selectedCartItem) {
          const modifierTags = splitPathToItem(value.pathToItem);
          const parentModifier =
            modifierTags.length > 1
              ? modifierTags[modifierTags.length - 2]
              : selectedCartItem?.name || '';
          if (
            isBMModifierAnUnavailableConstraint(
              selectedCartItem,
              unavailableItemsByName,
              parentModifier,
              value.pathToItem,
              value.parentModifierGroupDisplayName ||
                value.parentModifierGroupName ||
                selectedCartItem?.name ||
                '',
              value.name
            )
          )
            return true;
        }
      };

      const isOptionUnavailable = isUnavailableItem(value);
      const isUnavailable = isUnifiedMenuEnabled
        ? !value?.available || isOptionUnavailable || getBMUnavailability()
        : isOptionUnavailable;

      if (isUnavailable) {
        // The pick is refused either way — an 86'd item is 86'd whoever is
        // building — but the customer is only told about the ORDER.
        if (!value.name.startsWith('/') && !isSpeechRefused)
          sendMessage(getItemUnavailableMessage(value.name));
        return;
      }

      if (canUpsellInAIEscalation(value)) {
        return;
      }

      if (
        !value.name.includes('/upsell') &&
        (value.category === 'tts-prompt' || value.category.startsWith('/'))
      ) {
        // A slash command IS a spoken prompt — there is no cart half to let
        // through, so it is refused whole outside `Intercept`.
        if (!isSpeechRefused)
          sendSlashCommandMessage(value, isUnifiedMenuEnabled);
        setCurrentMessageAndVoiceBoardValue();
      } else if (value.category === 'modifier') {
        setCurrentMessageAndVoiceBoardValue();
        if (selectedCartItem && selectedItemCartId) {
          const menuItem = value as ParsedMenuItem;
          let { isChecked, modGroup } = processModifierSelection({
            cartItem: cart.cartItems[selectedItemCartId],
            menuItem,
            modSymbolMapping,
            selectedModGroup,
            inputModSymbol,
            navigationLevel,
            parentModGroupId: dialogState.parentModGroupId,
          });

          if (modGroup) {
            // Declined removes selected upsell item
            if (
              menuItem.name.toLocaleLowerCase() === 'declined' ||
              (menuItem.name.toLocaleLowerCase() === 'decline' &&
                (selectedCartItem.name.startsWith(UPSELL_PREFIX) ||
                  selectedCartItem.name.startsWith(UPSELL)))
            ) {
              isChecked = true;
              dispatch(
                selectedCartActions.deleteCartItem(
                  selectedItemCartId.toString()
                )
              );
              if (!isSelectedItemInDraft)
                sendAgentInterventionEvent(
                  AgentInterceptionType.ORDER_CHANGED,
                  `${selectedCartItem.name} removed from order`
                );
              const newCartItemId = findAnotherSelectedItem(
                cart.cartItems,
                selectedItemCartId
              );
              if (newCartItemId > 0) {
                dispatch(
                  dialogActions.selectItemAndResetNavigation({
                    item: cart.cartItems[newCartItemId.toString()],
                    itemCartId: newCartItemId,
                    cartScope: selectedCartScope,
                  })
                );
              } else {
                dispatch(dialogActions.selectItemAndResetNavigation(undefined));
              }
            } else {
              dispatch(
                selectedCartActions.selectModifier({
                  cartItemId: selectedItemCartId,
                  menuItemId: menuItem.itemId,
                  modGroupId: modGroup.id,
                  selected: isChecked,
                  modCode: inputModSymbol,
                  modSymbolMapping,
                  rootModGroupId:
                    navigationLevel === NAVIGATION_LEVELS.TOP
                      ? selectedModGroup?.id
                      : '',
                  posSettings,
                  pathToItem: menuItem.pathToItem,
                  readback: true,
                  applySuppressCascade: true,
                  suppressUpsellEnabled,
                  applyExpressCascade: expressUpsellMode || expressTimerActive,
                })
              );

              if (
                isBrandMenu &&
                inputModSymbol &&
                modSymbolMapping[inputModSymbol]
              ) {
                const modCodesGroupId = Object.keys(
                  menuItem.modifierGroups || {}
                ).filter((entry) =>
                  entry.toLocaleLowerCase().startsWith(MOD_CODES_GROUP_ID)
                )?.[0];

                if (modCodesGroupId) {
                  dispatch(
                    selectedCartActions.selectModifier({
                      cartItemId: selectedItemCartId,
                      menuItemId: modSymbolMapping[inputModSymbol].name,
                      modGroupId: modCodesGroupId,
                      selected: true,
                      modCode: '',
                      modSymbolMapping,
                      rootModGroupId: selectedModGroup?.id,
                      pathToItem: `${menuItem.pathToItem}__${menuItem.itemId}__${modCodesGroupId}`,
                      posSettings,
                      readback: true,
                      bmItemsSynonyms,
                      suppressUpsellEnabled,
                    })
                  );
                }
              }

              // Check for item conversion and option replacement after processing all modifier selection logic
              // This ensures the agent intervention event is sent for the conversion size selection
              handleModifierSelection(
                modGroup,
                menuItem.name,
                selectedCartItem
              );
            }
            // A draft edit is not an order event: emitting it would put a
            // modifier selection on the wire for an item that may never be
            // promoted.
            if (!isSelectedItemInDraft)
              sendModifierSelectionInfo({
                menuItem,
                modGroup: modGroup,
                selected: isChecked,
              });
          }
          dispatch(dialogActions.setHighlightedModifier(''));
        }
      } else {
        // HITL-1570: direct selection of a donation item whose constraint
        // declares speak_total_in_upsell, while no check total exists — skip
        // it entirely (no TTS, no cart add), matching the reminder-queue
        // path's invariant that a skipped donation is never offered.
        if (isDonationPromptSkipped(value)) {
          dispatch(dialogActions.setVoiceBoardValue(''));
          setInputQuantity(1);
          updateCurrentMessage('');
          setPrefixWord('');
          setHandleOutOfOrderSearch(false);
          return;
        }
        if (value.name.includes('/upsell') && !isUnifiedMenuEnabled) {
          // A false return means a gated donation was skipped: nothing was
          // spoken, so nothing may land in the cart either. (The guard above
          // already covers the known skip case; this keeps the invariant even
          // if the two gates ever drift.)
          const sent = sendSlashCommandMessage(value, isUnifiedMenuEnabled);
          updateCurrentMessage('');
          if (!sent) {
            dispatch(dialogActions.setVoiceBoardValue(''));
            setInputQuantity(1);
            setPrefixWord('');
            setHandleOutOfOrderSearch(false);
            return;
          }
        }
        dispatch(dialogActions.setVoiceBoardValue(''));

        const quantity = processQuantity(inputQuantity, value, currentMessage);
        // note: mod symbol never used on root items
        dispatch(
          addItemToCart({
            menuItem: value,
            prefixWord,
            inputModSymbol,
            quantity,
            // HITL-1619: a new item goes to the agent's workspace — the draft
            // in `Shadow`, the order in `Intercept`. The thunk pins an
            // `addedBy: AI` add to the order regardless, so the hypothesis
            // replay is unaffected by whatever the agent has selected.
            cartScope: newItemCartScope,
          })
        );
        if (!isBuildingOwnCart)
          sendAgentInterventionEvent(
            AgentInterceptionType.ORDER_CHANGED,
            `${value.name} added to cart`
          );

        setInputQuantity(1);
        updateCurrentMessage('');
        setPrefixWord('');
        setHandleOutOfOrderSearch(false);
      }
    }
  };

  const getPlaceHolder = () => getDialogMessage(false).message;

  const filterOptions = (
    options: (TopLevelMenuItem | ParsedMenuItem)[],
    state: FilterOptionsState<TopLevelMenuItem | ParsedMenuItem>
  ) => {
    const { quantity, inputValue, modSymbol } = getQuantityFromInput(
      state.inputValue,
      modSymbolMapping
    );

    setInputQuantity(quantity || 1);
    setInputModSymbol(modSymbol ? modSymbol : '');

    let resultList = getFilteredOptions(
      modSymbol,
      inputValue,
      quantity,
      options,
      state.inputValue
    );

    // Search for the second word if no result found for the whole input value and only hanlde two words for now. i.e. coke large
    // Also, if the input value starts with @ (which is used for customer name), then don't search for the second word
    const splittedInputValue = inputValue.trim().split(' ');
    if (
      !Object.values(resultList).length &&
      splittedInputValue.length === 2 &&
      !inputValue.startsWith(Symbols.at)
    ) {
      resultList = getFilteredOptions(
        modSymbol,
        splittedInputValue[1],
        quantity,
        options,
        state.inputValue
      );
      setPrefixWord(splittedInputValue[0]);
      setHandleOutOfOrderSearch(true);
    }

    const sorted = sortResults(resultList, isUnifiedMenuEnabled);

    if (
      !modSymbol &&
      shouldShowForceReadbackOmnibarHint({
        originalOmnibarInput: state.inputValue,
        isOCBReadbackEnabled,
      })
    ) {
      sorted.push(FORCE_READBACK_HINT_MENU_ITEM);
    }

    return sorted;
  };

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    menuItem: ParsedMenuItem | TopLevelMenuItem,
    state: AutocompleteRenderOptionState
  ) => {
    const optionProps = {
      htmlElemProps: props,
      menuItem,
      state,
      handleOutOfOrderSearch,
      canUpsellInAIEscalation,
      inputModSymbol,
    };
    return (
      <AutocompleteOption
        key={`${menuItem.id}${
          menuItem.categoryId ||
          (menuItem as ParsedMenuItem)?.parentModifierGroupId
        }`}
        {...optionProps}
      />
    );
  };

  const handleHighlightChange = (
    event: SyntheticEvent<Element, Event>,
    value: ParsedMenuItem | string | TopLevelMenuItem | null
  ) => {
    if (value instanceof Object && value.category === 'modifier') {
      const modifier = value as ParsedMenuItem;
      dispatch(dialogActions.setHighlightedModifier(modifier.pathToItem || ''));
    } else {
      dispatch(dialogActions.setHighlightedModifier(''));
    }
  };

  return (
    <Autocomplete
      data-testid="omnibar-autocomplete"
      freeSolo
      fullWidth
      // The input is narrow (Quick Response panel), so let the options surface
      // grow to fit the longest option instead of being capped to the input
      // width (which truncated labels like "...tts-prompt").
      componentsProps={{
        paper: {
          style: { width: 'max-content', maxWidth: '60vw' },
        },
      }}
      clearOnEscape
      open={currentMessage !== ''}
      autoHighlight={true}
      value={null}
      inputValue={currentMessage}
      onChange={onSelectChange}
      onHighlightChange={handleHighlightChange}
      options={menuItems}
      getOptionLabel={getOptionLabel}
      onClose={() => updateCurrentMessage('')}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={focusOmniBarInput}
          className={classes.wrapText}
          autoFocus
          id="omnitext"
          placeholder={getPlaceHolder()}
          onKeyPress={handleKeyPress}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          value={currentMessage}
          onChange={onTextChange}
          disabled={disabled}
          InputProps={{ ...params.InputProps, endAdornment: null }}
        />
      )}
      filterOptions={filterOptions}
      renderOption={renderOption}
    />
  );
};
